import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnDestroy, OnInit, ViewRef } from '@angular/core';
import { RouterModule } from '@angular/router';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { ChangableComponent } from '@app/models/changable.component';
import { RatingComponent } from '@app/shared/rating/rating.component';
import { AccountService, BillingV2Service, ConfigService, FilesService } from '@core/services';

import { IconComponent } from '@shared/icon/icon.component';

import { IAccountStats, ImageSizeEnum, Language, Subject, User, UserProfile } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiCover, OnUiCoverModule } from 'onclass-ui';
import { combineLatest, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export type TDisplayMode = 'wide' | 'middle' | 'narrow';

@Component({
  selector: 'app-user-mini-card',
  standalone: true,
  imports: [LocaleSharedModule, CommonModule, RouterModule, RatingComponent, OnUiCoverModule, IconComponent],
  templateUrl: './user-mini-card.component.html',
  styleUrls: ['./user-mini-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserMiniCardComponent extends ChangableComponent implements OnInit, OnDestroy {
  private configService = inject(ConfigService);
  private billingV2Service = inject(BillingV2Service);
  deviceService = inject(DeviceDetectorService);

  @Input() public set user(user: User) {
    this._user = user;
    this.init();
  }
  public get displayType() {
    return this._displayType;
  }
  @Input() public set displayType(type: TDisplayMode) {
    this._displayType = type;
    switch (type) {
      case 'narrow':
        this.avatarSize = ImageSizeEnum.lg;
        break;
      case 'middle':
        this.avatarSize = ImageSizeEnum.md;
        break;
      default:
        this.avatarSize = ImageSizeEnum.md;
    }
    this.init();
  }

  public avatarImgUrl: string;
  public avatarSize: ImageSizeEnum = ImageSizeEnum.md;
  public countryFlagUrl: string;
  public countryName: string;
  public fullName: string;
  public accountRoute: string[];
  public stats: Partial<IAccountStats>;
  public teachingSubjects: string[];
  public speakLanguages: Language[] = [];
  public about: string;
  public createdAt: Date | string;
  public avatar: OnUiCover;
  public isTeacher: boolean;
  public profileVerified: boolean;

  private _displayType: TDisplayMode = 'wide';
  private _user: User;
  private _userProfile: UserProfile;
  private _subjects: Subject[];
  private _languages: Language[];

  ngOnInit(): void {
    combineLatest([this.configService.subjects$, this.configService.languages$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ([subjects, languages]) => {
          this._subjects = subjects;
          this._languages = languages;
          this.init();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  private init() {
    if (!this._user || !this._subjects) {
      return;
    }
    this._userProfile = this._user.profile as UserProfile;
    this.isTeacher = AccountService.isAsIfTeacher(this._user);
    this.profileVerified = this._user.teacher_profile_verified;
    this.about = this._userProfile.about;
    this.teachingSubjects = (this._userProfile?.teaching_subjects || [])
      .filter((ts) => ts.subject_id > 0)
      .map((subject) => {
        const _subject = this._subjects.find((s) => s.id === +subject.subject_id);
        return (_subject ? _subject.title : null) || '---';
      });
    this.speakLanguages = this._userProfile?.spoken_languages
      .sort((a, b) => +a.level_id - +b.level_id)
      .map((_sl) => this._languages.find((_lang) => +_lang.id === +_sl.language_id));
    this.avatarImgUrl = FilesService.getFileUrlBySize(this._user.userpic_id, this.avatarSize);
    this.countryFlagUrl = this._user?.country?.flag_url;
    this.countryName = this._user?.country?.code;
    this.fullName = AccountService.getUserFullName(this._user);
    this.accountRoute = AccountService.accountRoute(this._user);
    this.avatar = {
      type: 'avatar',
      img: this.avatarImgUrl,
      title: this.fullName,
      form: 'circle',
    };
    this.stats = this._user.stats;
    this.createdAt = this._user.created_at;
    this.detectDataChanges();
  }
  public getFormattedPrice(amount: number): Observable<string> {
    return this.billingV2Service.formatAmount$(amount);
  }

  public detectDataChanges() {
    if (this.cdr && !(this.cdr as ViewRef).destroyed) {
      this.cdr.markForCheck();
    }
  }

  public trackByFn(index) {
    return index;
  }
}
